import { useState } from 'react';

import type { LoginEventResult } from '../../api/types';
import { useLoginEvents, useTeamMembers } from '../../hooks/use-team';
import { cn } from '../../lib/cn';
import { Select } from '../ui/select';
import { Skeleton } from '../ui/skeleton';

const RESULT_LABELS: Record<LoginEventResult, string> = {
  success: 'Вход выполнен',
  bad_password: 'Неверный пароль',
  disabled: 'Учётная запись отключена',
  rate_limited: 'Пауза после перебора',
};

/** Настройки → Входы (owner): the latest login attempts, optionally for one member. */
export function LoginEventsSection() {
  const [userId, setUserId] = useState('');
  const { data: team } = useTeamMembers();
  const { data, isLoading, isError } = useLoginEvents(userId || undefined);
  const items = data?.items ?? [];

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <label className="text-sm" htmlFor="login-events-user">
          Сотрудник
        </label>
        <Select id="login-events-user" value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Все, включая неизвестные email</option>
          {(team?.members ?? []).map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name ? `${m.name} — ${m.email}` : m.email}
            </option>
          ))}
        </Select>
      </div>
      <p className="mb-3 text-xs text-muted-fg">
        Последние 100 попыток входа. После 10 неудач за 15 минут вход по этому email приостанавливается на 15 минут.
      </p>

      {isLoading && <Skeleton className="h-40 w-full" />}
      {isError && <div className="text-sm text-red-600">Не удалось загрузить журнал входов.</div>}
      {!isLoading && !isError && (
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase text-muted-fg">
            <tr>
              <th className="px-3 py-2">Время</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Результат</th>
              <th className="px-3 py-2">IP</th>
              <th className="px-3 py-2">Устройство</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-b border-border">
                <td className="whitespace-nowrap px-3 py-2 text-xs">{new Date(e.at).toLocaleString('ru-RU')}</td>
                <td className="px-3 py-2">
                  {e.email}
                  {e.name && <span className="ml-2 text-xs text-muted-fg">{e.name}</span>}
                  {!e.userId && <span className="ml-2 text-xs text-muted-fg">нет такой учётной записи</span>}
                </td>
                <td className={cn('px-3 py-2', e.result === 'success' ? 'text-emerald-700' : 'text-red-600')}>
                  {RESULT_LABELS[e.result] ?? e.result}
                </td>
                <td className="px-3 py-2 text-xs text-muted-fg">{e.ip ?? '—'}</td>
                <td className="max-w-xs truncate px-3 py-2 text-xs text-muted-fg" title={e.userAgent ?? undefined}>
                  {e.userAgent ?? '—'}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-sm text-muted-fg">
                  Попыток входа пока нет
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
